import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Tooltip,
    Autocomplete,
    Divider,
} from '@mui/material';
import sessionService from '../../services/session.service';
import apiService from '../../services/api.service';
import { AuthContext } from '../../contexts/AuthContext';
import { Icon as OpusIcon } from '../common/opus';
import PlsCompetitionFixturePicker from '../Renderer/PlsCompetitionFixturePicker';
import { createDdsPlsCatalogue } from '../../services/data/adapters/DdsPlsCatalogue';
import { getDDSClient } from '../../lib/ddsClient';
import { toDateTimeLocalValue } from '../../utils/dateTimeLocal';
import { SESSION_NAME_MAX_LENGTH, getSessionDisplayName, validateSessionName } from '../../utils/sessionName';
import MaestroSetupFields from './MaestroSetupFields';
import {
    supportsMaestro,
    maestroFormFromSession,
    previewRendererUrlFor,
    retargetPreviewUrl,
    toMaestroPayload,
    sessionMaestroPayload,
    isSameMaestro,
    validateMaestroForm,
    fieldsForFlavour,
    FALLBACK_MAESTRO_FIELDS,
} from '../../utils/maestroSettings';

const rendererKey = (r) => r._id || r.id;
const rendererLabel = (r) => r.renderer_name || r.name || rendererKey(r) || '';

// Paired fields sit side by side once the dialog is wide enough.
const TWO_COLUMNS_SX = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, alignItems: 'start' };

const formFromSession = (session) => ({
    sessionName: getSessionDisplayName(session),
    rendererId: session?.rendererId || '',
    fixtureId: session?.fixtureId || '',
    fixtureName: session?.fixtureName || '',
    fixtureStartTime: toDateTimeLocalValue(session?.fixtureStartTime),
    fixtureEndTime: toDateTimeLocalValue(session?.fixtureEndTime),
});

// ─── Edit Session Dialog ──────────────────────────────────────────────────────
// Rename a session and change its renderer / fixture / Maestro setup from the
// session list, without going into the session details page. Only fields that actually
// changed are PATCHed, so an unchanged renderer never triggers a backend
// renderer reassignment or fixture time-clash check.
//
// `knownRenderers` / `knownCompetitions` / `knownSeasons` let the parent hand
// over lists it has already fetched, so opening the dialog doesn't re-request
// them (and the fields render filled-in instead of behind a loader).
// `maestroFields` is the configured Maestro field list (useSessionFields);
// only the fields for the session's app are used.
const EditSessionDialog = ({
    open, session, knownRenderers = [], knownCompetitions = [], knownSeasons = null,
    maestroFields: allMaestroFields = FALLBACK_MAESTRO_FIELDS, onClose, onSaved, onError,
}) => {
    const { userInfo } = useContext(AuthContext);
    const maestroFields = fieldsForFlavour(allMaestroFields, session?.applicationFlavour);
    // Read inside the open-effect only — a parent re-render with fresh arrays
    // must not reset a form the user is already editing.
    const knownRef = useRef({ knownRenderers, knownCompetitions, knownSeasons, maestroFields });
    knownRef.current = { knownRenderers, knownCompetitions, knownSeasons, maestroFields };

    const [form, setForm] = useState(() => formFromSession(session));
    const [maestro, setMaestro] = useState(() => maestroFormFromSession(session));
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [renderers, setRenderers] = useState([]);
    const [renderersLoading, setRenderersLoading] = useState(false);

    // Competition/season browse state for the fixture picker, seeded from
    // the session's own applicationDetails rather than the create dialog's
    // persisted browse state.
    const [competitions, setCompetitions] = useState([]);
    const [competitionId, setCompetitionId] = useState('');
    const [seasons, setSeasons] = useState([]);
    const [seasonId, setSeasonId] = useState('');
    const fixtureCatalogue = useMemo(() => createDdsPlsCatalogue({
        client: getDDSClient(),
        competitionId,
        seasonId,
    }), [competitionId, seasonId]);

    useEffect(() => {
        if (!open || !session) return undefined;
        let cancelled = false;

        setForm(formFromSession(session));
        setErrors({});
        const seededCompetitionId = session.applicationDetails?.competitionId || '';
        setCompetitionId(seededCompetitionId);
        setSeasonId(session.applicationDetails?.seasonId || '');

        const { knownRenderers: knownRends, knownCompetitions: comps, knownSeasons: known, maestroFields: fields } = knownRef.current;
        const currentRenderer = knownRends.find((r) => rendererKey(r) === session.rendererId);
        setMaestro(maestroFormFromSession(session, previewRendererUrlFor(currentRenderer), fields));
        if (knownRends.length) {
            setRenderers(knownRends);
        } else {
            const orgField = userInfo?.data?.organization_id;
            const orgId = (typeof orgField === 'object' && orgField !== null) ? orgField._id : orgField;
            setRenderersLoading(true);
            apiService.getRenderersByOrganization(orgId)
                .then((res) => { if (!cancelled) setRenderers(res?.data?.renderers || res?.renderers || []); })
                .catch(() => { if (!cancelled) setRenderers([]); })
                .finally(() => { if (!cancelled) setRenderersLoading(false); });
        }

        const catalogue = createDdsPlsCatalogue({ client: getDDSClient() });
        if (comps.length) {
            setCompetitions(comps);
        } else {
            catalogue.getCompetitions()
                .then((rows) => { if (!cancelled) setCompetitions(rows || []); })
                .catch(() => { if (!cancelled) setCompetitions([]); });
        }
        if (!seededCompetitionId) {
            setSeasons([]);
        } else if (known?.competitionId === seededCompetitionId && known.seasons?.length) {
            setSeasons(known.seasons);
        } else {
            catalogue.getSeasons(seededCompetitionId)
                .then((rows) => { if (!cancelled) setSeasons(rows || []); })
                .catch(() => { if (!cancelled) setSeasons([]); });
        }

        return () => { cancelled = true; };
    }, [open, session, userInfo]);

    const clearFixture = (f) => ({ ...f, fixtureId: '', fixtureName: '', fixtureStartTime: '', fixtureEndTime: '' });

    const handleCompetitionChange = async (id) => {
        setCompetitionId(id);
        setSeasonId('');
        setForm(clearFixture);
        const rows = await createDdsPlsCatalogue({ client: getDDSClient() }).getSeasons(id).catch(() => []);
        setSeasons(rows || []);
    };

    const handleSeasonChange = (id) => {
        setSeasonId(id);
        setForm(clearFixture);
    };

    // Owner-locked renderers can't be newly assigned, but the session's
    // current renderer always stays selectable. Renderers already "in_use"
    // are allowed — the backend rejects overlapping fixture windows
    // (FIXTURE_TIME_CLASH) at save time.
    const selectableRenderers = renderers.filter((r) =>
        rendererKey(r) === session?.rendererId || !r.owner_lock?.locked
    );
    const currentRendererMissing = Boolean(session?.rendererId) && !renderersLoading
        && !renderers.some((r) => rendererKey(r) === session.rendererId);

    // The session already carries its fixture's id/name/start and its
    // competition/season names — show them straight away rather than waiting
    // for the picker's lists to load. Names only apply while the browsed
    // competition/season is still the session's own.
    const fixtureFallback = session?.fixtureId
        ? { id: session.fixtureId, label: session.fixtureName || session.fixtureId, startDateTime: session.fixtureStartTime }
        : null;
    const storedDetails = session?.applicationDetails || {};
    const storedCompetitionName = competitionId === storedDetails.competitionId ? storedDetails.competitionName : null;
    const storedSeasonName = seasonId === storedDetails.seasonId ? storedDetails.seasonName : null;

    const nameChanged = form.sessionName.trim() !== getSessionDisplayName(session);
    const rendererChanged = form.rendererId !== (session?.rendererId || '');
    const fixtureChanged = form.fixtureId !== (session?.fixtureId || '');
    const showMaestro = supportsMaestro(session?.applicationFlavour);
    const maestroPayload = toMaestroPayload(maestro, maestroFields);
    const maestroChanged = showMaestro && !isSameMaestro(maestroPayload, sessionMaestroPayload(session, maestroFields));
    const findRenderer = (id) => renderers.find((r) => rendererKey(r) === id);

    const validate = () => {
        const errs = {};
        const nameError = validateSessionName(form.sessionName);
        if (nameError) errs.sessionName = nameError;
        if (rendererChanged && !form.rendererId) errs.rendererId = 'Required';
        if (fixtureChanged && !form.fixtureId) errs.fixtureId = 'Required';
        if (showMaestro) {
            const maestroErrs = validateMaestroForm(maestro, maestroFields);
            if (Object.keys(maestroErrs).length) errs.maestro = maestroErrs;
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;
        if (!nameChanged && !rendererChanged && !fixtureChanged && !maestroChanged) {
            onClose();
            return;
        }

        const patch = {};
        if (nameChanged) patch.sessionName = form.sessionName.trim();
        if (rendererChanged) patch.rendererId = form.rendererId || null;
        // null clears the setup so operators fall back to their own settings.
        if (maestroChanged) patch.maestro = maestroPayload;
        if (fixtureChanged) {
            Object.assign(patch, {
                fixtureId: form.fixtureId,
                fixtureName: form.fixtureName || null,
                fixtureStartTime: form.fixtureStartTime ? new Date(form.fixtureStartTime).toISOString() : null,
                fixtureEndTime: form.fixtureEndTime ? new Date(form.fixtureEndTime).toISOString() : null,
                // PATCH replaces applicationDetails wholesale — keep other keys
                // and record which competition/season the new fixture came from.
                applicationDetails: {
                    ...(session.applicationDetails || {}),
                    competitionId,
                    competitionName: competitions.find((c) => c.id === competitionId)?.name || null,
                    seasonId,
                    seasonName: seasons.find((s) => s.id === seasonId)?.name || null,
                },
            });
        }

        setSaving(true);
        try {
            const res = await sessionService.updateSession(session.sessionId, patch);
            onSaved(res.data);
        } catch (err) {
            onError(err.message || 'Failed to update session');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
            <DialogTitle>Edit Session</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>
                    <Typography sx={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-dim)' }}>
                        {session?.sessionId}
                    </Typography>
                    <Box sx={TWO_COLUMNS_SX}>
                        <TextField
                            autoFocus
                            size="small"
                            label="Session Name"
                            value={form.sessionName}
                            onChange={(e) => setForm((f) => ({ ...f, sessionName: e.target.value }))}
                            error={!!errors.sessionName}
                            helperText={errors.sessionName}
                            inputProps={{ maxLength: SESSION_NAME_MAX_LENGTH }}
                            fullWidth
                        />
                        <Autocomplete
                            options={selectableRenderers}
                            size="small"
                            getOptionLabel={rendererLabel}
                            value={selectableRenderers.find((r) => rendererKey(r) === form.rendererId) || null}
                            onChange={(_, v) => {
                                const previous = findRenderer(form.rendererId);
                                setForm((f) => ({ ...f, rendererId: v ? rendererKey(v) : '' }));
                                setMaestro((m) => retargetPreviewUrl(m, previewRendererUrlFor(previous), previewRendererUrlFor(v)));
                            }}
                            disabled={renderersLoading}
                            isOptionEqualToValue={(o, v) => rendererKey(o) === rendererKey(v)}
                            renderOption={(props, r) => {
                                const { key, ...rest } = props;
                                const isLocked = r.owner_lock?.locked === true;
                                return (
                                    <Box component="li" key={key} {...rest} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography sx={{ fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {rendererLabel(r)}
                                        </Typography>
                                        {isLocked && (
                                            <Tooltip title={r.owner_lock?.locked_by_name ? `Locked by ${r.owner_lock.locked_by_name}` : 'Locked'}>
                                                <Box sx={{ display: 'flex', color: 'var(--coral)', flexShrink: 0 }}>
                                                    <OpusIcon name="lock" size={13} />
                                                </Box>
                                            </Tooltip>
                                        )}
                                    </Box>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Renderer"
                                    required
                                    error={!!errors.rendererId}
                                    helperText={errors.rendererId
                                        || (currentRendererMissing && !rendererChanged ? 'The current renderer has been deleted' : undefined)}
                                />
                            )}
                        />
                    </Box>
                    <Box>
                        <PlsCompetitionFixturePicker
                            catalogue={fixtureCatalogue}
                            wide
                            competitions={competitions}
                            competitionId={competitionId}
                            onCompetitionChange={handleCompetitionChange}
                            seasons={seasons}
                            seasonId={seasonId}
                            onSeasonChange={handleSeasonChange}
                            selectedCompetitionName={storedCompetitionName}
                            selectedSeasonName={storedSeasonName}
                            selectedFixtureId={form.fixtureId}
                            selectedFixtureFallback={fixtureFallback}
                            onSelect={(id, label, option) => setForm((f) => ({
                                ...f,
                                fixtureId: id || '',
                                fixtureName: label || '',
                                // Times always come from the picker's selection, never
                                // typed — see DdsPlsCatalogue._endDateTime for the
                                // end-time fallback before a match has concluded.
                                fixtureStartTime: option?.startDateTime ? toDateTimeLocalValue(option.startDateTime) : '',
                                fixtureEndTime: option?.endDateTime ? toDateTimeLocalValue(option.endDateTime) : '',
                            }))}
                        />
                        {errors.fixtureId && (
                            <Typography sx={{ color: 'error.main', fontSize: 12, mt: 0.5, ml: 1.5 }}>
                                {errors.fixtureId}
                            </Typography>
                        )}
                    </Box>
                    {form.fixtureId && (
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Fixture start time"
                                type="datetime-local"
                                InputLabelProps={{ shrink: true }}
                                value={form.fixtureStartTime}
                                InputProps={{ readOnly: true }}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                label="Fixture end time"
                                type="datetime-local"
                                InputLabelProps={{ shrink: true }}
                                value={form.fixtureEndTime}
                                InputProps={{ readOnly: true }}
                            />
                        </Box>
                    )}
                    {showMaestro && (
                        <>
                            <Divider sx={{ my: 0.5 }} />
                            <MaestroSetupFields
                                fields={maestroFields}
                                value={maestro}
                                onChange={setMaestro}
                                errors={errors.maestro}
                                hasSuggestedPreview={Boolean(previewRendererUrlFor(findRenderer(form.rendererId)))}
                            />
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="outlined" color="info" disabled={saving}>
                    Cancel
                </Button>
                <Button onClick={handleSave} variant="outlined" color="seafoamGreen" disabled={saving} startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}>
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditSessionDialog;
